import { LoanStatus } from '@roc/feature-utils';
import {
  ClosingStage,
  LoanDetailsTab,
  TodoItem,
  TodoStatus,
} from './loanDetailsTypes';
import { TaskPriority } from 'libs/feature-general-tasks/src/generalTasks/types/generalTasksTypes';

export const statusOptions = [
  {
    label: 'Pending',
    value: TodoStatus.PENDING,
  },
  {
    label: 'Being Reviewed',
    value: TodoStatus.BEING_REVIEWED,
  },
  {
    label: 'Accepted',
    value: TodoStatus.ACCEPTED,
  },
  {
    label: 'More Info Needed',
    value: TodoStatus.MORE_INFO_NEEDED,
  },
  {
    label: 'Not Applicable',
    value: TodoStatus.NOT_APPLICABLE,
  },
];

export const stepsByLoanStatus = {
  [LoanStatus.PENDING]: [
    { title: 'Submission', completed: true },
    { title: 'Pending To-dos', completed: true, active: true },
    { title: 'Underwriting' },
    { title: 'Committee' },
    { title: 'Approved' },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.IN_UNDERWRITING]: [
    { title: 'Submission', completed: true },
    { title: 'Pending To-dos', completed: true },
    { title: 'Underwriting', completed: true, active: true },
    { title: 'Committee' },
    { title: 'Approved' },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.PENDING_DECISION]: [
    { title: 'Submission', completed: true },
    { title: 'Underwriting', completed: true },
    { title: 'Committee', completed: true, active: true },
    { title: 'Approved' },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.AGREED]: [
    { title: 'Submission', completed: true },
    { title: 'Underwriting', completed: true },
    { title: 'Committee', completed: true },
    { title: 'Approved', completed: true },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.FUNDED]: [
    { title: 'Submission', completed: true },
    { title: 'Underwriting', completed: true },
    { title: 'Committee', completed: true },
    { title: 'Approved', completed: true },
    { title: 'Funded', completed: true },
    { title: 'Paid Off' },
  ],
  [LoanStatus.PAID_OFF]: [
    { title: 'Submission', completed: true },
    { title: 'Underwriting', completed: true },
    { title: 'Committee', completed: true },
    { title: 'Approved', completed: true },
    { title: 'Funded', completed: true },
    { title: 'Paid Off', completed: true },
  ],
  [LoanStatus.PAID_OFF_BUYOUT]: [
    { title: 'Submission', completed: true },
    { title: 'Underwriting', completed: true },
    { title: 'Committee', completed: true },
    { title: 'Approved', completed: true },
    { title: 'Funded', completed: true },
    { title: 'Paid Off', completed: true },
  ],
  [LoanStatus.DECLINED]: [
    { title: 'Submission', completed: true },
    { title: 'Declined', completed: true, negative: true },
  ],
  [LoanStatus.MOVED_TO_EXTERNAL_CAPITAL]: [
    { title: 'Submission', completed: true },
    { title: 'Moved to External Capital', completed: true, negative: true },
  ],
  [LoanStatus.RETRACTED]: [
    { title: 'Submission', completed: true },
    { title: 'Retracted', completed: true, negative: true },
  ],
  [LoanStatus.ON_HOLD]: [
    { title: 'Submission', completed: true },
    { title: 'On Hold', completed: true, active: true },
    { title: 'Retracted' },
  ],
  [LoanStatus.PENDING_SUBMISSION]: [
    { title: 'Pending Submission', completed: true, warning: true },
    { title: 'Underwriting' },
    { title: 'Committee' },
    { title: 'Approved' },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.APPLICATION_SUBMITTED]: [
    { title: 'Application Submitted', completed: true },
    { title: 'Pending To-dos', completed: true, active: true },
    { title: 'Underwriting' },
    { title: 'Committee' },
    { title: 'Approved' },
    { title: 'Funded' },
    { title: 'Paid Off' },
  ],
  [LoanStatus.APPLICATION_REJECTED]: [
    { title: 'Application Submitted', completed: true },
    { title: 'Application Rejected', completed: true, negative: true },
  ],
};

export const stepsByClosingStatus = {
  [ClosingStage.UNDEFINED]: [
    { title: 'Loan Agreed', completed: false },
    { title: 'Title Contacted', completed: false },
    { title: 'Closing Scheduled', completed: false },
    { title: 'Pending Approval', completed: false },
    { title: 'Approved to Fund', completed: false },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.LOAN_AGREED]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: false },
    { title: 'Closing Scheduled', completed: false },
    { title: 'Pending Approval', completed: false },
    { title: 'Approved to Fund', completed: false },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.TITLE_CONTACTED]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: false },
    { title: 'Pending Approval', completed: false },
    { title: 'Approved to Fund', completed: false },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.CLOSING_SCHEDULED]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: false },
    { title: 'Approved to Fund', completed: false },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.PENDING_APPROVAL]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: false },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.APPROVED_TO_FUND]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: true },
    { title: 'Docs Sent for Signing', completed: false },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.DOCS_SENT_FOR_SIGNING]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: true },
    { title: 'Docs Sent for Signing', completed: true },
    { title: 'Wire Requested', completed: false },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.WIRE_REQUESTED]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: true },
    { title: 'Docs Sent for Signing', completed: true },
    { title: 'Wire Requested', completed: true },
    { title: 'Wire Sent', completed: false },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.WIRE_SENT]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: true },
    { title: 'Docs Sent for Signing', completed: true },
    { title: 'Wire Requested', completed: true },
    { title: 'Wire Sent', completed: true },
    { title: 'Loan Closed & Funded', completed: false },
  ],
  [ClosingStage.LOAN_CLOSED_AND_FUNDED]: [
    { title: 'Loan Agreed', completed: true },
    { title: 'Title Contacted', completed: true },
    { title: 'Closing Scheduled', completed: true },
    { title: 'Pending Approval', completed: true },
    { title: 'Approved to Fund', completed: true },
    { title: 'Docs Sent for Signing', completed: true },
    { title: 'Wire Requested', completed: true },
    { title: 'Wire Sent', completed: true },
    { title: 'Loan Closed & Funded', completed: true },
  ],
};

enum DocumentStatus {
  PENDING = 'OPEN',
  BEING_REVIEWED = 'IN_REVIEW',
  MORE_INFO_NEEDED = 'MORE_INFO_NEEDED',
  ACCEPTED = 'COMPLETED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  REPORTS_DELIVERED = 'Report(s) Delivered',
  CANCELED = 'Canceled',
}

export const todoStatusMapping: Record<Exclude<TodoStatus, TodoStatus.ALL>, DocumentStatus> = {
  [TodoStatus.ACCEPTED]: DocumentStatus.ACCEPTED,
  [TodoStatus.PENDING]: DocumentStatus.PENDING,
  [TodoStatus.MORE_INFO_NEEDED]: DocumentStatus.MORE_INFO_NEEDED,
  [TodoStatus.NOT_APPLICABLE]: DocumentStatus.NOT_APPLICABLE,
  [TodoStatus.BEING_REVIEWED]: DocumentStatus.BEING_REVIEWED,
  [TodoStatus.REPORTS_DELIVERED]: DocumentStatus.REPORTS_DELIVERED,
  [TodoStatus.CANCELED]: DocumentStatus.CANCELED,
};

export const StatusFilterType = {
  IN_REVIEW_OR_PENDING: [
    TodoStatus.BEING_REVIEWED,
    TodoStatus.PENDING,
    TodoStatus.MORE_INFO_NEEDED,
  ].join(','),
  ALL_ACTIVE: [
    TodoStatus.PENDING,
    TodoStatus.MORE_INFO_NEEDED,
    TodoStatus.BEING_REVIEWED,
    TodoStatus.ACCEPTED,
  ].join(','),
  INTERNAL_ONLY: 'INTERNAL_ONLY',
  PENDING: TodoStatus.PENDING,
  ALL: TodoStatus.ALL,
};

export const PriorityFilterType = {
  CRITICAL: [
    TaskPriority.VERY_CRITICAL,
    TaskPriority.CRITICAL,
  ].join(','),
};

export const SHOW_IN_REVIEW_AND_PENDING_TO_DOS = 'Show In Review and Pending To-dos';
export const SHOW_ALL_ACTIVE_TO_DOS = 'Show All Active To-dos';
export const SHOW_INTERNAL_TO_DOS = 'Show Internal To-dos';
export const SHOW_CRITICAL_PENDING_TO_DOS = 'Show Critical Pending To-dos';
export const SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS = 'Show Critical In Review & Pending To-dos';
export const SHOW_PENDING_TO_DOS = 'Show Pending To-dos';
export const SHOW_ALL_TO_DOS = 'Show All To-dos';

export const statusFilterDropdownExternalOptionsV1 = [
  { label: SHOW_PENDING_TO_DOS, value: SHOW_PENDING_TO_DOS },
  { label: SHOW_ALL_TO_DOS, value: SHOW_ALL_TO_DOS },
];

export const statusFilterDropdownInternalOptionsV1 = [
  { label: SHOW_IN_REVIEW_AND_PENDING_TO_DOS, value: SHOW_IN_REVIEW_AND_PENDING_TO_DOS },
  { label: SHOW_ALL_ACTIVE_TO_DOS, value: SHOW_ALL_ACTIVE_TO_DOS },
  { label: SHOW_INTERNAL_TO_DOS, value: SHOW_INTERNAL_TO_DOS },
  { label: SHOW_PENDING_TO_DOS, value: SHOW_PENDING_TO_DOS },
  { label: SHOW_ALL_TO_DOS, value: SHOW_ALL_TO_DOS },
];

export const statusFilterDropdownExternalOptionsV2 = [
  { label: SHOW_CRITICAL_PENDING_TO_DOS, value: SHOW_CRITICAL_PENDING_TO_DOS },
  { label: SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS, value: SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS },
  { label: SHOW_PENDING_TO_DOS, value: SHOW_PENDING_TO_DOS },
  { label: SHOW_ALL_TO_DOS, value: SHOW_ALL_TO_DOS },
];

export const mapStatusFilter = {
  [SHOW_IN_REVIEW_AND_PENDING_TO_DOS]: StatusFilterType.IN_REVIEW_OR_PENDING,
  [SHOW_ALL_ACTIVE_TO_DOS]: StatusFilterType.ALL_ACTIVE,
  [SHOW_INTERNAL_TO_DOS]: StatusFilterType.INTERNAL_ONLY,
  [SHOW_CRITICAL_PENDING_TO_DOS]: StatusFilterType.PENDING,
  [SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS]: StatusFilterType.IN_REVIEW_OR_PENDING,
  [SHOW_PENDING_TO_DOS]: StatusFilterType.PENDING,
  [SHOW_ALL_TO_DOS]: StatusFilterType.ALL,
}

export const mapApiShowOnlyPending = {
  [SHOW_IN_REVIEW_AND_PENDING_TO_DOS]: false,
  [SHOW_ALL_ACTIVE_TO_DOS]: false,
  [SHOW_INTERNAL_TO_DOS]: false,
  [SHOW_CRITICAL_PENDING_TO_DOS]: true,
  [SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS]: false,
  [SHOW_PENDING_TO_DOS]: true,
  [SHOW_ALL_TO_DOS]: false,
}

export const mapPriorityFilter = {
  [SHOW_IN_REVIEW_AND_PENDING_TO_DOS]: [],
  [SHOW_ALL_ACTIVE_TO_DOS]: [],
  [SHOW_INTERNAL_TO_DOS]: [],
  [SHOW_CRITICAL_PENDING_TO_DOS]: PriorityFilterType.CRITICAL,
  [SHOW_CRITICAL_IN_REVIEW_AND_PENDING_TO_DOS]: PriorityFilterType.CRITICAL,
  [SHOW_PENDING_TO_DOS]: [],
  [SHOW_ALL_TO_DOS]: [],
}

export enum InternalValuationReviewRequired {
  NOT_REQUIRED = "Not Required",
  NOT_REQUIRED_MANUALLY_OVERWRITTEN = "Not Required - Manually Overwritten",
  IV_PILOT_PROGRAM = "realval program",
  FLAGGED_BY_SYSTEM = "Flagged by System",
  FLAGGED_BY_CREDIT = "Flagged by Credit"
}
export const IV_PILOT_PROGRAM = "realval program";

export const ESCALATED_TASK_TYPE = 'ESCALATED';
export const TASK_ACCESS_TAG_TYPE = 'TASK_ACCESS';
export const INTERNAL_TAG_VALUE = 'INTERNAL';
